import {localized, msg} from '@lit/localize';
import {customElement, property} from 'lit/decorators.js';
import {html, LitElement} from 'lit';
import type {
  AutocompleteProps,
  ElementSearch,
  SearchResult,
} from './element-search.ts';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {escapeRegExp, replaceAll} from '../helpers/string.ts';
import {classMap} from 'lit/directives/class-map.js';
import {minusSvg, plusSvg} from '../helpers/elements.ts';

@localized()
@customElement('element-search-result')
export class ElementSearchResult extends LitElement {
  @property({type: Object}) result: SearchResult;
  @property({type: Object}) props: AutocompleteProps;
  @property({type: Object}) parent: ElementSearch;

  override render() {
    const attrs = this.result.attributes;
    const category = attrs.type === 'geoadmin' ? 'geoadmin' : attrs.kind;
    const info = this.parent.listInfo[category];
    // Match input value except if the string is inside an HTML tag.
    const regexp = new RegExp(
      `${escapeRegExp(this.parent.autocomplete.input.value)}(?![^<>]*>)`,
      'ig',
    );
    let title = replaceAll(this.result.title, '<b>', '');
    title = replaceAll(title, '</b>', '');
    const label = title.replace(
      regexp,
      (match: string) =>
        `<span class='font-bold bg-chm-light-gray'>${match}</span>`,
    );
    const logo =
      attrs.type === 'fts' &&
      (attrs.ftsType === 'segment' || attrs.ftsType === 'route')
        ? attrs.logo
        : undefined;
    let subtitle = html``;
    let subtitleContent: string;
    if (attrs.type === 'fts') {
      if (attrs.addressCity) {
        subtitleContent = attrs.addressCity;
      } else if (attrs.ftsType === 'route') {
        subtitleContent = `${attrs.start} - ${
          attrs.via ? `${attrs.via} - ` : ''
        } ${attrs.end}`;
      }
      if (subtitleContent) {
        const subtitleLabel = subtitleContent.replace(
          regexp,
          (match: string) =>
            `<span class='font-bold bg-chm-light-gray'>${match}</span>`,
        );
        // probably OK since this is from the FTS (WebDB data)
        // would not be OK if it were a track imported from some other user
        subtitle = html`<div>${unsafeHTML(subtitleLabel)}</div>`;
      }
    }

    return html`
      ${this.result.index > 0
        ? ''
        : html` <li
            class="distinction mx-[42px] mt-[20px] flex h-[40px] items-center border-y-[1px] border-y-dark-divider font-bold sm:mx-[54px] md:h-[49px] mdxx:mx-[70px]"
          >
            ${attrs.groupTitle()} (${info.length})
          </li>`}
      <li
        id="${this.props.id}"
        data-result-index="${this.props['data-result-index']}"
        role="${this.props.role}"
        tabindex="0"
        class="autocomplete-result md:search-result-m ${classMap({
          hidden: this.result.index > 2 && !info.showAll,
        })} mx-[42px] cursor-pointer select-none border-b-[1px] border-b-chm-light-gray text-search-result-s sm:mx-[54px] mdxx:mx-[70px]"
        @keydown="${(e: KeyboardEvent) => {
          if (e.key === 'Enter') (<HTMLElement>e.target).click();
        }}"
      >
        <div
          class="grid min-h-[40px] grid-cols-4 items-center text-body sm:text-sm-body md:grid-cols-8 md:text-md-body"
        >
          <div
            class="col-span-4 flex pb-[12px] pt-[8px] md:col-span-5 md:items-center"
          >
            ${!logo
              ? ''
              : html` <img
                  class="mr-[13px] h-[24px] w-[24px] bg-cover bg-no-repeat md:my-[4px] md:mr-[22px] md:h-[40px] md:w-[40px]"
                  src="${logo}"
                  loading="lazy"
                />`}
            <div class="mt-[4px] flex flex-col md:mt-0">
              <div>
                ${
                  // FIXME: would not not safe if some day we search for own tracks
                  unsafeHTML(label)
                }
              </div>
              <div class="md:hidden">${subtitle}</div>
            </div>
          </div>
          <div class="hidden md:col-span-3 md:block">${subtitle}</div>
        </div>
      </li>
      ${this.result.index === 2 && !info.showAll
        ? html` <li
            tabindex="0"
            class="mx-[42px] flex h-[40px] cursor-pointer items-center text-url sm:mx-[54px] md:h-[49px] md:text-md-url mdxx:mx-[70px]"
            @click="${() => this.parent.showAll(category)}"
            @keydown="${(e: KeyboardEvent) => {
              if (e.key === 'Enter') this.parent.showAll(category);
            }}"
          >
            ${plusSvg('w-[18px] h-[18px] fill-chm-red mr-[11px]')}
            ${msg('Show all')}
          </li>`
        : ''}
      ${this.result.index === info.length - 1 && info.showAll
        ? html` <li
            tabindex="0"
            class="mx-[42px] flex h-[40px] cursor-pointer items-center text-url sm:mx-[54px] md:h-[49px] md:text-md-url mdxx:mx-[70px]"
            @click="${() => this.parent.showLess(category)}"
            @keydown="${(e: KeyboardEvent) => {
              if (e.key === 'Enter') this.parent.showLess(category);
            }}"
          >
            ${minusSvg('w-[18px] h-[18px] fill-chm-red mr-[11px]')}
            ${msg('Show less')}
          </li>`
        : ''}
    `;
  }

  override createRenderRoot() {
    return this;
  }
}
